/**
 * <AgentProgressHeader>
 *
 * Agent Progress 页顶部 Header 区。基于 Figma 节点 9281-13754 重写。
 *
 * 视觉结构（Figma 还原）：
 *  ┌──────────────────────────────────────────────────────────────────────┐
 *  │ {Title}  [StatusBadge]  ═══════════════════ {NN%}    ⏸ Pause ✕ ... │
 *  │ Date Created: Oct 24, 2024                                         │
 *  └──────────────────────────────────────────────────────────────────────┘
 *
 * 进度条使用 <ProgressBar> 线性进度条 + 百分比文字，替代旧的圆形进度。
 */

import { FC } from 'react';
import cn from 'classnames';
import { Pencil, History, X, CirclePlay, CirclePause } from 'lucide-react';
import { Button } from '@leadsnavi/ui-kit';
import { ProgressBar } from '@/common/biz/progress-bar';
import { InquiryStatusBadge } from '@/common/biz/status-tag/InquiryStatusBadge';
import type { AgentProgressHeaderVM } from '../../types';

export type AgentProgressHeaderProps = {
  vm: AgentProgressHeaderVM;
  /** 点击 Pencil → 打开重命名弹窗 */
  onOpenNameEdit: () => void;
  /** 点击 Request Details 图标按钮 */
  onOpenRequestDetails: () => void;
  /** 点击关闭按钮 */
  onClose?: () => void;
  /** 可选：点击 History 图标按钮（Figma 含此入口；page 未实现时不传） */
  onClickHistory?: () => void;
  /** 点击 Pause/Resume 按钮 */
  onPauseResume?: () => void;
  /** Pause/Resume 进行中时禁用按钮，避免重复触发 */
  operating?: boolean;
  className?: string;
};

export const AgentProgressHeader: FC<AgentProgressHeaderProps> = ({
  vm,
  onOpenNameEdit,
  // onOpenRequestDetails,
  onClose,
  onClickHistory,
  onPauseResume,
  operating = false,
  className,
}) => {
  const { title, status, statusLabel, progressPct, dateCreatedDisplay, canRename, canPauseResume } = vm;

  return (
    <header className={cn('flex flex-col gap-2 mb-5', className)}>
      {/* 第一行：标题 + 状态 + 进度条 + 操作按钮 */}
      <div className="flex items-center gap-4">
        {/* 左侧：标题 + Pencil + StatusBadge + ProgressBar */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Title + Pencil */}
          <div className="group flex items-center gap-1 min-w-0">
            <h1 className="text-xl font-semibold text-[#1D1C1B] tracking-tight whitespace-nowrap truncate min-w-0">
              {title}
            </h1>
            {canRename && (
              <button
                type="button"
                onClick={onOpenNameEdit}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 cursor-pointer"
                aria-label="Edit inquiry name"
              >
                <Pencil size={14} strokeWidth={1.75} />
              </button>
            )}
          </div>

          {/* StatusBadge */}
          <InquiryStatusBadge status={status} text={statusLabel} />

          {/* ProgressBar（线性） + 百分比 */}
          <div className="flex items-center gap-2 w-[150px] shrink-0">
            <ProgressBar
              progress={progressPct}
              text={`${progressPct}%`}
              showText={true}
              completedColor="#10B981"
              remainingColor="#E2E2E2"
            />
          </div>
        </div>

        {/* 右侧：操作按钮组 */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Pause / Resume */}
          {canPauseResume && onPauseResume && (
            <Button
              onClick={onPauseResume}
              loading={operating}
              disabled={operating}
              className={
                status === 'paused'
                  ? 'flex items-center gap-[8px] bg-[#F99A2C] hover:bg-[#FBBE78] disabled:opacity-40 text-white px-[16px] py-[7.5px] rounded-[8px]'
                  : 'flex items-center gap-[8px] bg-[#262626] hover:bg-[#171717] disabled:opacity-40 text-white px-[16px] py-[7.5px] rounded-[8px]'
              }
            >
              {status === 'paused' ? (
                <>
                  <CirclePlay size={18} />
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <CirclePause size={18} />
                  <span>Pause</span>
                </>
              )}
            </Button>
          )}

          {/* History（可选） */}
          {onClickHistory && (
            <Button variant="ghost" size="sm" onClick={onClickHistory} aria-label="Open history" className="px-2">
              <History size={16} strokeWidth={1.75} />
            </Button>
          )}

          {/* Close 按钮 */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-[#262626] hover:text-[#171717] transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X size={20} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>

      {/* 第二行：Date Created */}
      <p className="text-xs text-[#737373] tracking-wide">{dateCreatedDisplay}</p>
    </header>
  );
};

export default AgentProgressHeader;
